import { Chart, ChartConfiguration } from 'chart.js'
import { getFontSize } from '../../../../utils/getFontSize'
import { datasetDefaults } from '../../../electricity/chartUtils/chartConfigs/initialChartOptions'
import { ChartTimeHook } from '../../../electricity/chartUtils/hooks/useChartTime'

export const createSolarPanelChart = (
	chartElement: any,
	chartData: number[],
	time: ChartTimeHook,
	darkModeEnabled: boolean
) => {
	if (time.span === 'day') return dayChart(chartElement, chartData, time.span, darkModeEnabled)
	else if (time.span === 'week')
		return weekChart(chartElement, chartData, time.span, darkModeEnabled)
	else if (time.span === 'month')
		return monthChart(chartElement, chartData, time.span, darkModeEnabled)
	else return yearChart(chartElement, chartData, time.span, darkModeEnabled)
}

const getDataset = (chartData: number[], darkModeEnabled: boolean) => ({
	...datasetDefaults,
	label: 'kWh',
	backgroundColor: darkModeEnabled ? '#3cb49f' : '#127d27',
	data: chartData,
	barPercentage: 0.8,
	borderRadius: 2,
})

const defaultOptions = (chartData: number[], span: string, darkModeEnabled: boolean) => {
	const gridColor = darkModeEnabled ? '#144448' : '#f5f5f5'
	const xTickColor = darkModeEnabled ? '#e3e3e3' : '#858585'
	const yTickColor = darkModeEnabled ? '#3cb49f' : '#127d27'

	const fontSize = getFontSize()

	return {
		type: 'bar',
		data: {
			labels: [...Array(chartData.length).keys()].map((x: number) => ('0' + x).slice(-2)),
			datasets: [getDataset(chartData, darkModeEnabled)],
		},
		options: {
			maintainAspectRatio: false,
			scales: {
				y: {
					border: {
						display: false,
					},
					ticks: {
						color: yTickColor,
						font: {
							weight: 400,
						},
					},
					grid: {
						color: gridColor,
					},
				},
				x: {
					border: {
						display: false,
					},
					grid: {
						display: false,
					},
					ticks: {
						minRotation: 45,
						maxRotation: 45,
						maxTicksLimit: span === 'day' ? 8 : span === 'month' ? 8 : 6,
						color: xTickColor,
						border: { display: false },
						font: { weight: 400 },
						labelOffset: 5,
						padding: -3,
					},
				},
			},
			plugins: {
				tooltip: {
					titleFont: {
						size: fontSize + 2,
						weight: 'bold',
					},
					bodyFont: {
						size: fontSize,
					},
				},
				legend: {
					display: false,
					reverse: true,
				},
			},
		},
	} as ChartConfiguration
}

const dayChart = (
	chartElement: HTMLCanvasElement,
	chartData: number[],
	span: string,
	darkModeEnabled: boolean
) => new Chart(chartElement, { ...defaultOptions(chartData, span, darkModeEnabled) })

const weekChart = (
	chartElement: HTMLCanvasElement,
	chartData: number[],
	span: string,
	darkModeEnabled: boolean
) =>
	new Chart(chartElement, {
		...defaultOptions(chartData, span, darkModeEnabled),
		data: {
			labels: [
				'Maanantai',
				'Tiistai',
				'Keskiviikko',
				'Torstai',
				'Perjantai',
				'Lauantai',
				'Sunnuntai',
			].splice(0, chartData.length),
			datasets: [getDataset(chartData, darkModeEnabled)],
		},
	})

const monthChart = (
	chartElement: HTMLCanvasElement,
	chartData: number[],
	span: string,
	darkModeEnabled: boolean
) =>
	new Chart(chartElement, {
		...defaultOptions(chartData, span, darkModeEnabled),
		data: {
			labels: Array.from({ length: chartData.length }, (_, i) => (i + 1).toString()),
			datasets: [getDataset(chartData, darkModeEnabled)],
		},
	})

const yearChart = (
	chartElement: HTMLCanvasElement,
	chartData: number[],
	span: string,
	darkModeEnabled: boolean
) =>
	new Chart(chartElement, {
		...defaultOptions(chartData, span, darkModeEnabled),
		data: {
			labels: [
				'Tammikuu',
				'Helmikuu',
				'Maaliskuu',
				'Huhtikuu',
				'Toukokuu',
				'Kesäkuu',
				'Heinäkuu',
				'Elokuu',
				'Syyskuu',
				'Lokakuu',
				'Marraskuu',
				'Joulukuu',
			].slice(0, chartData.length),
			datasets: [getDataset(chartData, darkModeEnabled)],
		},
	})
